/** Open-field battlefield scatter — the rocks and trees a field fight is had over.
 *
 * A siege draws its masonry from `siegeObstacles`, which is a pure function of
 * the fort level. An open field has no such structure to derive, so the props
 * are rolled: this file is the kernel's one generator for them, and everything
 * that wants a field board — the adventure kernel's three battle openings, the
 * debug scenes, a test — calls it rather than writing a second one.
 *
 * What is generated is what the rules block on. A `rock` and a `tree` are both
 * hard cover (see `obstacleBlocks`); the renderer picks the *sprite* per hex out
 * of the arena's own palette from `art: ""`, so the kernel names no artwork and
 * the two never disagree about which cell is impassable.
 *
 * Measured off the shipped game (`CLASSIC_Battlefield.jpg`, an open grass field,
 * and the classic gallery's `classic11.jpg`): 10 blocked hexes on the first — eight
 * single rock clusters, one bush, one dead trunk, spread singly — and roughly 18
 * on the second, gathered into two multi-hex formations (a lake with a rocky rim
 * and a ridge running off it) plus three loose props. That is 6.1% and ~10.9% of
 * the 165 cells, and it is the reason placement here grows small blobs rather
 * than scattering isolated cells: the classic does both, and a board of nothing but
 * singletons reads as gravel rather than terrain.
 */

import { type RngStreamState, nextBounded, nextRange } from "../rng/pcg32.js";
import {
  BF_HEIGHT, BF_HEXES, BF_WIDTH, type Obstacle, type ObstacleKind,
  hexCol, hexIndex, hexNeighbours, hexRow,
} from "./state.js";

/**
 * The stream field obstacles are rolled off.
 *
 * Its own stream, and not a prefix of `battle.ability`, which is what it
 * borrowed while `RNG_STREAMS` was closed to this file. Sharing one stream
 * between two consumers couples them in a direction nothing declares: the
 * scatter takes a variable number of draws - a rejected formation costs more
 * than an accepted one - so every board would have shifted the ability rolls of
 * the battle fought on it, and any later change to how many rolls an ability
 * takes would have silently reshuffled every battlefield.
 *
 * It is rolled once, before the first round, and nothing else draws from
 * `battle.field`, so the advanced state carried into `BattleState.rng` is inert
 * for the rest of the fight. That is what lets a lookahead reproduce the exact
 * board the kernel will build from the same stream state.
 */
export const FIELD_OBSTACLE_STREAM = "battle.field";

/**
 * The columns props may stand in.
 *
 * Columns 0-2 and 12-14 stay bare. Both armies deploy on the outer column, a
 * two-hex stack's head lands one column in, and a war machine spills to the
 * second column when seven stacks have filled the first - so 0 and 1 (13 and
 * 14) are start hexes and may never carry an obstacle. The third column is
 * given as forming-up room, which is also where the reference boards start
 * putting props: the leftmost prop on `CLASSIC_Battlefield.jpg` sits about two
 * columns in from the attacker's line.
 */
export const FIELD_MIN_COL = 3;
export const FIELD_MAX_COL = BF_WIDTH - 4;

/** Blocked cells to aim for, out of the 165 on the board. See the header. */
const FIELD_MIN_CELLS = 12;
const FIELD_MAX_CELLS = 18;

/** Cells in one formation. Three is the widest thing the references show. */
const FIELD_MAX_BLOB = 3;

/**
 * Ground that costs something to cross, laid after the blocking props.
 *
 * The props are cover: they refuse a cell and shape where a line can form.
 * These are the other half of the same idea and the half the classic does not have - a
 * cell you *may* enter and would rather not. Mud costs two movement points
 * (`hexStepCost`), embers burn whoever stops in them (`hazardBite`), and both
 * are laid on the same board both armies fight over, so neither is an advantage
 * to whoever rolled it.
 *
 * They are deliberately fewer and smaller than the props. A board where a third
 * of the middle is slow stops being a board with terrain on it and becomes a
 * board with a wall down the middle - the walking pass would route round it
 * every time and the patch would never be a decision. Two to five cells, in
 * runs of at most two, is enough to make one lane worse than another.
 */
const FIELD_MIN_GROUND = 2;
const FIELD_MAX_GROUND = 5;
const GROUND_MAX_RUN = 2;

/** Enough tries that a target is normally met, bounded so this always returns. */
const FIELD_TRIES = 220;

const interiorHexes = (): number[] => {
  const out: number[] = [];
  for (let row = 0; row < BF_HEIGHT; row++) {
    for (let col = FIELD_MIN_COL; col <= FIELD_MAX_COL; col++) out.push(hexIndex(col, row));
  }
  return out;
};

/**
 * Flood the free cells from `start` and say whether every free cell was found.
 *
 * `wide` models a two-hex stack: its node is the *head* hex, and the head is
 * only standable when the tail column beside it is free too. Facing +1 is the
 * attacker (tail to the left), -1 the defender. A corridor one cell wide is
 * passable to a single-hex stack and not to a Lancer, which is why the two
 * questions are asked separately.
 */
function freeGraphConnected(blocked: Uint8Array, facing: 0 | 1 | -1): boolean {
  const ok = new Uint8Array(BF_HEXES);
  let total = 0;
  for (let h = 0; h < BF_HEXES; h++) {
    if (blocked[h] === 1) continue;
    if (facing !== 0) {
      const tailCol = hexCol(h) - facing;
      if (tailCol < 0 || tailCol >= BF_WIDTH) continue;
      if (blocked[h - facing] === 1) continue;
    }
    ok[h] = 1;
    total++;
  }
  if (total === 0) return false;

  let start = -1;
  for (let h = 0; h < BF_HEXES; h++) if (ok[h] === 1) { start = h; break; }

  const seen = new Uint8Array(BF_HEXES);
  const queue = new Int32Array(BF_HEXES);
  const nb: number[] = [0, 0, 0, 0, 0, 0];
  let head = 0, tail = 0, found = 1;
  seen[start] = 1;
  queue[tail++] = start;
  while (head < tail) {
    const h = queue[head++]!;
    const k = hexNeighbours(h, nb);
    for (let m = 0; m < k; m++) {
      const n = nb[m]!;
      if (seen[n] === 1 || ok[n] !== 1) continue;
      seen[n] = 1;
      found++;
      queue[tail++] = n;
    }
  }
  return found === total;
}

/**
 * Is this board still one board?
 *
 * Every free cell has to be reachable from every other, for a single-hex stack
 * and for a two-hex stack of either facing. That is stronger than "the two
 * armies can meet" and it is the version worth checking: it also rules out a
 * pocket that would strand a stack the tactics phase walked into, and a shooter
 * fenced off behind its own cover.
 */
export function fieldPlayable(blocked: Uint8Array): boolean {
  return freeGraphConnected(blocked, 0)
    && freeGraphConnected(blocked, 1)
    && freeGraphConnected(blocked, -1);
}

/**
 * Roll the scatter for one open-field battle.
 *
 * Returns the obstacles and the advanced stream, which the caller writes back
 * into the battle's own `rng` - the roll is a roll like any other and has to
 * show up in state (I10, D5).
 *
 * A formation is only kept if the board is still playable with it on, so the
 * invariant holds by construction rather than by a density argument.
 */
export function fieldObstacles(rng: RngStreamState): [readonly Obstacle[], RngStreamState] {
  let st = rng;
  const interior = interiorHexes();
  const [afterTarget, target] = nextRange(st, FIELD_MIN_CELLS, FIELD_MAX_CELLS);
  st = afterTarget;

  const blocked = new Uint8Array(BF_HEXES);
  // The ring around every formation already standing. Formations are kept
  // apart by one clear cell, which is what both reference boards show: props
  // arrive as distinct things - a single rock, a stump, a two-cell thicket -
  // and never as an unbroken run. It also means a formation can never fuse with
  // its neighbour into a fence the connectivity check then has to unpick.
  const halo = new Uint8Array(BF_HEXES);
  const cells: number[] = [];
  const blob: number[] = [];
  const nb: number[] = [0, 0, 0, 0, 0, 0];

  for (let tries = 0; tries < FIELD_TRIES && cells.length < target; tries++) {
    let pick: number;
    [st, pick] = nextBounded(st, interior.length);
    const seed = interior[pick]!;
    if (blocked[seed] === 1 || halo[seed] === 1) continue;

    let want: number;
    const room = target - cells.length;
    [st, want] = nextRange(st, 1, room < FIELD_MAX_BLOB ? room : FIELD_MAX_BLOB);

    blob.length = 0;
    blob.push(seed);
    blocked[seed] = 1;
    while (blob.length < want) {
      // Candidates are gathered in hex order so the choice depends on the roll
      // and not on the order the frontier happened to grow in.
      const cand: number[] = [];
      for (const h of blob) {
        const k = hexNeighbours(h, nb);
        for (let m = 0; m < k; m++) {
          const n = nb[m]!;
          const c = hexCol(n);
          if (c < FIELD_MIN_COL || c > FIELD_MAX_COL) continue;
          if (blocked[n] === 1 || halo[n] === 1 || cand.includes(n)) continue;
          cand.push(n);
        }
      }
      if (cand.length === 0) break;
      cand.sort((a, b) => a - b);
      let g: number;
      [st, g] = nextBounded(st, cand.length);
      const grown = cand[g]!;
      blocked[grown] = 1;
      blob.push(grown);
    }

    if (fieldPlayable(blocked)) {
      for (const h of blob) {
        cells.push(h);
        const k = hexNeighbours(h, nb);
        for (let m = 0; m < k; m++) halo[nb[m]!] = 1;
      }
    } else {
      for (const h of blob) blocked[h] = 0;
    }
  }

  cells.sort((a, b) => a - b);
  const out: Obstacle[] = [];

  // The hazard ground. Rolled from the same stream and after the props, so a
  // board's props are byte-identical to what they were before hazards existed
  // given the same seed - the draws that pick them happen first.
  //
  // `blocked` is not written here: hazard ground does not block, so it cannot
  // strand anything and `fieldPlayable` has nothing to say about it. What it
  // must not do is share a cell with a prop, which `blocked` already answers.
  const hazard: number[] = [];
  const hazardKind: ObstacleKind[] = [];
  {
    let want: number;
    [st, want] = nextRange(st, FIELD_MIN_GROUND, FIELD_MAX_GROUND);
    for (let tries = 0; tries < FIELD_TRIES && hazard.length < want; tries++) {
      let pick: number;
      [st, pick] = nextBounded(st, interior.length);
      const seed = interior[pick]!;
      if (blocked[seed] === 1 || hazard.includes(seed)) continue;
      let kindRoll: number;
      [st, kindRoll] = nextBounded(st, 100);
      const kind: ObstacleKind = kindRoll < 60 ? "mud" : "embers";
      let run: number;
      [st, run] = nextRange(st, 1, GROUND_MAX_RUN);
      hazard.push(seed);
      hazardKind.push(kind);
      // A run grows along the column, the way a puddle or a burn scar does -
      // across the lanes the armies advance down rather than along one of them.
      for (let g = 1; g < run && hazard.length < want; g++) {
        const row = hexRow(seed) + g;
        if (row >= BF_HEIGHT) break;
        const h = hexIndex(hexCol(seed), row);
        if (blocked[h] === 1 || hazard.includes(h)) break;
        hazard.push(h);
        hazardKind.push(kind);
      }
    }
  }

  for (const hex of cells) {
    // Kind is flavour: `obstacleBlocks` refuses both, and the renderer picks the
    // sprite out of the arena palette by hex, not by kind. It is rolled anyway
    // so a rules change that ever separates the two has something to separate.
    let k: number;
    [st, k] = nextBounded(st, 100);
    out.push({ kind: k < 38 ? "tree" : "rock", hex, integrity: 1, art: "" });
  }
  // Sorted into the same hex order the props are in, so the obstacle list has
  // one ordering rule and a hash does not depend on which pass laid a cell.
  const ground: Obstacle[] = [];
  for (let i = 0; i < hazard.length; i++) {
    ground.push({ kind: hazardKind[i]!, hex: hazard[i]!, integrity: 1, art: "" });
  }
  ground.sort((a, b) => a.hex - b.hex);
  for (const g of ground) out.push(g);
  return [out, st];
}
